import React from 'react'
import {
  Styles as DayPickerStyles,
  DayPicker as ReactDayPicker,
} from 'react-day-picker'
import 'react-day-picker/style.css'
import { useTranslation } from 'react-i18next'
import { AvailabilitySlot } from '../calendar-interface'
import { CalendarDaySlot } from './calendar-day-slot'
import { MonthCaption } from './month-caption'
import { CalendarWrapperUi } from './ui/calendar-wrapper-ui'
import { DayPickerWrapperUi } from './ui/day-picker-wrapper-ui'
import { WeekdayUi } from './ui/weekday-ui'

const CALENDAR_GRID_PADDINGS = 20
interface DayPickerProps {
  selectedDate?: Date
  onDateSelect?: (date: Date) => void
  selectedMonth?: Date
  onMonthSelect?: (month: Date) => void
  isLoading?: boolean

  availabilitySlots: AvailabilitySlot[]
  availableSlotBackgroundColor: string
  selectedSlotBackgroundColor: string
  titlesColor?: string
  mobileTitlesColor?: string
  color?: string
  mobileColor?: string
  displayTimezone?: string
  isMobile?: boolean
}

export const DayPicker = ({
  selectedDate,
  onDateSelect,
  selectedMonth = new Date(),
  onMonthSelect,
  isLoading,

  availabilitySlots,
  displayTimezone,

  availableSlotBackgroundColor,
  selectedSlotBackgroundColor,
  titlesColor,
  mobileTitlesColor,
  color,
  mobileColor,
  isMobile,
}: DayPickerProps) => {
  const { t } = useTranslation()

  return (
    <CalendarWrapperUi isDateSelected={!!selectedDate}>
      <span style={{ color: titlesColor, fontWeight: 600 }}>
        {t('components.core.calendar.select_date')}
      </span>
      <DayPickerWrapperUi>
        <ReactDayPicker
          showOutsideDays
          timeZone={displayTimezone}
          disableNavigation={!onMonthSelect}
          defaultMonth={selectedMonth}
          onMonthChange={month => onMonthSelect?.(month)}
          mode="single"
          selected={selectedDate}
          styles={getStyles({
            titlesColor: isMobile ? mobileTitlesColor : titlesColor,
            color: isMobile ? mobileColor : color,
            gridPaddings: isMobile ? 0 : CALENDAR_GRID_PADDINGS,
          })}
          labels={{
            labelWeekday: date => String(date.getDay()),
          }}
          components={{
            Weekday: WeekdayUi,
            Day: props => (
              <CalendarDaySlot
                selectedDate={selectedDate}
                availableSlotBackgroundColor={availableSlotBackgroundColor}
                selectedSlotBackgroundColor={selectedSlotBackgroundColor}
                textColor={isMobile ? mobileColor : color}
                onDateSelect={onDateSelect}
                isLoading={isLoading}
                {...props}
              />
            ),
            MonthCaption: props => (
              <MonthCaption
                yearColor={isMobile ? mobileColor : color}
                {...props}
              />
            ),
          }}
          modifiers={{
            isDateAvailable: availabilitySlots.map(
              ({ date }) => new Date(date),
            ),
          }}
          modifiersStyles={{
            today: {
              color: isMobile ? mobileColor : color,
            },
          }}
        />
      </DayPickerWrapperUi>
    </CalendarWrapperUi>
  )
}

interface GetStylesArgs {
  titlesColor?: string
  color?: string
  gridPaddings?: number
}

function getStyles({
  titlesColor,
  color,
  gridPaddings,
}: GetStylesArgs): Partial<DayPickerStyles> {
  return {
    month_grid: {
      width: '100%',
      minHeight: '350px',
      borderCollapse: 'separate',
      borderSpacing: 0,
      padding: gridPaddings,
    },
    month: {
      width: '100%',
    },
    months: {
      maxWidth: '100%',
    },
    weekday: {
      color: titlesColor,
      fontWeight: 500,
      height: 20,
      padding: 0,
    },
    day_button: {
      width: '100%',
    },
    month_caption: {
      color: titlesColor,
      borderBottom: '1px solid rgba(181, 195, 213, 0.5)',
      padding: '0 15px',
      height: 40,
      alignItems: 'center',
      fontWeight: 400,
      fontSize: 16,
    },
    nav: {
      right: '5px',
    },
    day: {
      color,
    },
  }
}
